import sanitizeHtml from 'sanitize-html'

/**
 * This functions takes in a number and returns it with a maximum of two decimals.
 * @param number - Number to limit decimals.
 * */
export const limitDecimals = (number: number) => {
  if (isNaN(number)) {
    return undefined
  }
  if (Number.isInteger(number)) {
    return number
  }
  return parseFloat(number.toFixed(2))
}

/**
 * @param value number parameter
 * @param compact boolean parameter
 * @returns number compact notation formatter (K, M, T)
 */
export const abbreviateNumber = (params: {
  value: number
  compact?: boolean
}) => {
  const { value, compact = false } = params

  const formatter = new Intl.NumberFormat('en', {
    maximumSignificantDigits: 6,
    notation: 'compact'
  })
  const format = formatter.format(value)
  const key = format.slice(-1)

  const filter = format.substring(0, format.length - 1)
  const split = filter.split('.')

  let number = split[0]
  if (split[1]) {
    const decimal = split[1][0].toString()
    if (decimal !== '0') {
      number = `${number}.${split[1][0]}`
    }
  }

  let notation: string
  switch (key) {
    case 'K':
      notation = compact ? 'K' : 'Mil'
      break
    case 'M':
      notation = compact ? 'M' : 'Millones'
      break
    default:
      notation = ''
  }

  return {
    number,
    notation
  }
}

export const numberWithCommaWithoutDecimals = (
  value: any,
  withSymbol?: boolean
) => {
  const parse = new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })?.format(value)

  const result = parse.replace('$', '')

  return `${withSymbol ? '$ ' : ''}${result}`
}

export const stringNumberFormatToSimpleNumber = (value: string) => {
  const newValue = value.replace(/[^0-9]|\$|,/g, '').trim()
  return parseInt(newValue, 10)
}

/**
 * @param value number parameter
 * @returns number compact notation formatter (K, M)
 */
export const reduceNumber = (value: number) => {
  const reduceBy = value >= 1_000_000 ? 1_000_000 : 1_000
  const reduceName = value >= 1_000_000 ? 'M' : 'K'
  const reduced = value / reduceBy
  return `$${parseFloat(reduced.toFixed(1))}${reduceName}`
}

export function formatPhoneNumber(input: string): string | null {
  if (!input?.length) {
    return null
  }

  const cleanInput = input?.replace(/[^0-9]/g, '')

  if (cleanInput?.length >= 10) {
    const lastTenDigits = cleanInput?.substring(cleanInput?.length - 10)
    return `${lastTenDigits?.substring(0, 2)}-${lastTenDigits?.substring(
      2,
      6
    )}-${lastTenDigits?.substring(6)}`
  }

  return input
}

export const onFormatDescription = (description: string) => {
  const formatText = description
    ?.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
    .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br>')

  return sanitizeHtml(formatText ?? '', {
    allowedTags: ['b', 'i', 'em', 'strong', 'a', 'br']
  })
}

export const formatNumberWithDecimals = ({
  value,
  onChange,
  maxLength = 12
}: {
  value: string | number | null
  onChange: (value: string | null) => void
  maxLength?: number
}) => {
  if (!value || value?.toString()?.trim()?.length === 0) {
    onChange(null)
    return
  }

  const cleanedValue = value
    ?.toString()
    ?.replace(/[^0-9.]/g, '')
    ?.replace(/\.+/g, '.')
    ?.replace(/^\./, '0.')

  if (maxLength && cleanedValue?.length > maxLength) {
    onChange(cleanedValue?.slice(0, maxLength))
    return
  }

  if (/^\d*\.?\d*$/.test(cleanedValue) && cleanedValue?.includes('.')) {
    const [integerPart, decimalPart] = cleanedValue?.split('.')
    const formattedValue = `${integerPart}.${decimalPart?.slice(0, 2) || ''}`
    onChange(formattedValue)
    return
  }

  onChange(cleanedValue || null)
}

export const getDaysUntilExpiration = (expiresAt: string): number => {
  if (!expiresAt || typeof expiresAt !== 'string' || expiresAt.trim() === '') {
    return 0
  }

  try {
    const [day, month, year] = expiresAt.split('/').map(Number)

    if (isNaN(day) || isNaN(month) || isNaN(year)) {
      return 0
    }

    if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900) {
      return 0
    }

    const expirationDate = new Date(year, month - 1, day)

    if (isNaN(expirationDate.getTime())) {
      return 0
    }

    const currentDate = new Date()

    expirationDate.setHours(0, 0, 0, 0)
    currentDate.setHours(0, 0, 0, 0)

    const diffInMs = expirationDate.getTime() - currentDate.getTime()
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24))

    return isNaN(diffInDays) ? 0 : diffInDays
  } catch (error) {
    return 0
  }
}
